import {
  DRAFT_DESCRIPTION_MAX_LENGTH,
  QUOTA_ERROR_COPY,
  TITLE_MAX_LENGTH
} from "./constants";
import { getErrorMessage, getErrorName } from "./safeError";

export function nowIso(): string {
  return new Date().toISOString();
}

export function createId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

export function sanitizePlainText(input: string, maxLength = DRAFT_DESCRIPTION_MAX_LENGTH): string {
  return stripControlCharacters(input)
    .replace(/\s+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim()
    .slice(0, maxLength);
}

function stripControlCharacters(input: string): string {
  return Array.from(input, (character) => {
    const code = character.charCodeAt(0);
    const allowedWhitespace = code === 9 || code === 10 || code === 13;
    return (code < 32 && !allowedWhitespace) || code === 127 ? " " : character;
  }).join("");
}

export function sanitizeTitle(input: string): string {
  return sanitizePlainText(input, TITLE_MAX_LENGTH);
}

export function isQuotaError(error: unknown): boolean {
  const name = getErrorName(error);
  const message = getErrorMessage(error);
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    /quota|storage\s+full|not\s+enough\s+space/i.test(message)
  );
}

export function isWebKitBlobStorageError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes("blob/file data") && message.includes("object") && message.includes("store");
}

export function isMediaStoragePreparationError(error: unknown): boolean {
  const name = getErrorName(error);
  const message = getErrorMessage(error);
  return (
    isWebKitBlobStorageError(error) ||
    name === "DataCloneError" ||
    (
      (name === "UnknownError" || name === "AbortError" || name === "InvalidStateError") &&
      (/indexeddb|blob|file|clone/i.test(message) ||
        (message.toLowerCase().includes("object") && message.toLowerCase().includes("store")))
    )
  );
}

export function getMediaSaveErrorCopy(error: unknown, mediaLabel: "photo" | "recording" = "photo"): string {
  if (isQuotaError(error)) {
    return QUOTA_ERROR_COPY;
  }
  if (isMediaStoragePreparationError(error)) {
    return `This browser had trouble saving the ${mediaLabel} on this device. Try again after closing extra tabs or freeing device storage.`;
  }
  return `Could not save this ${mediaLabel} on this device. Try again.`;
}

export function slugifyFilenamePart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function makeLocalTitle(description: string): string {
  const clean = sanitizePlainText(description, 240);
  if (!clean) {
    return "Condition note";
  }
  const firstSentence = clean.split(/[.!?\n]/)[0] ?? clean;
  const words = firstSentence.split(/\s+/).filter(Boolean).slice(0, 9);
  const title = words.join(" ");
  const trimmed = title.length > 60 ? `${title.slice(0, 57).trim()}...` : title;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isNonEmptyText(value: string | undefined): boolean {
  return Boolean(value && value.trim().length > 0);
}
